import { useEffect, useRef, type RefObject } from 'react'
import { useFixerRainScroll } from '../hooks/ui/useFixerRainScroll.js'
import './fixer-ascii-rain.css'

const ASCII_CHARSET = '01.:·+-|#*FB/c'
const FONT_SIZE = 13
const DRIP_PER_SCROLL = 0.85

interface RainColumn {
  y: number
  speed: number
  char: string
  brightness: number
}

interface FixerAsciiRainProps {
  scrollTop?: number
  intensity?: number
  reducedMotion?: boolean
  isDarkMode?: boolean
}

function pickChar(): string {
  return ASCII_CHARSET[Math.floor(Math.random() * ASCII_CHARSET.length)] ?? '·'
}

function readReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function FixerAsciiRain({
  scrollTop = 0,
  intensity = 0,
  reducedMotion = false,
  isDarkMode = true,
}: FixerAsciiRainProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const scrollTopRef = useRef(scrollTop)
  const intensityRef = useRef(intensity)

  scrollTopRef.current = scrollTop
  intensityRef.current = intensity

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return

    let width = 0
    let height = 0
    let columns: RainColumn[] = []
    let raf = 0
    let lastTime = 0
    const motionReduced = reducedMotion || readReducedMotion()

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = canvas.clientWidth
      height = canvas.clientHeight
      canvas.width = Math.floor(width * dpr)
      canvas.height = Math.floor(height * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const columnCount = Math.max(12, Math.floor(width / FONT_SIZE))
      columns = Array.from({ length: columnCount }, (_, i) => ({
        y: Math.random() * height * -0.5 - i * 4,
        speed: motionReduced ? 0.35 : 0.6 + Math.random() * 1.2,
        char: pickChar(),
        brightness: 0.35 + Math.random() * 0.65,
      }))
    }

    const draw = (time: number) => {
      const delta = Math.min(32, time - lastTime)
      lastTime = time

      const drip = scrollTopRef.current * DRIP_PER_SCROLL
      const rainStrength = intensityRef.current

      if (rainStrength <= 0.01) {
        ctx.clearRect(0, 0, width, height)
        raf = requestAnimationFrame(draw)
        return
      }

      const trailAlpha = 0.07 * rainStrength
      const headR = isDarkMode ? 201 : 140
      const headG = isDarkMode ? 95 : 52
      const headB = isDarkMode ? 58 : 38
      const dimR = isDarkMode ? 168 : 74
      const dimG = isDarkMode ? 173 : 78
      const dimB = isDarkMode ? 176 : 82

      ctx.fillStyle = isDarkMode
        ? `rgba(5, 6, 6, ${trailAlpha})`
        : `rgba(232, 233, 235, ${trailAlpha * 0.85})`
      ctx.fillRect(0, 0, width, height)

      ctx.font = `${FONT_SIZE}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`
      ctx.textBaseline = 'top'

      const step = motionReduced ? delta * 0.018 : delta * 0.05

      for (let i = 0; i < columns.length; i++) {
        const col = columns[i]
        if (!col) continue
        col.y += col.speed * step * (0.65 + rainStrength * 0.55)

        const x = i * FONT_SIZE
        const y = (col.y + drip) % (height + FONT_SIZE * 16)

        if (col.y > height + FONT_SIZE * 10) {
          col.y = -Math.random() * height * 0.4
          col.char = pickChar()
          col.speed = motionReduced ? 0.35 : 0.6 + Math.random() * 1.2
          col.brightness = 0.35 + Math.random() * 0.65
        }

        if (Math.random() < 0.02) col.char = pickChar()

        const trailSteps = 12
        for (let t = 0; t < trailSteps; t++) {
          const ty = y - t * FONT_SIZE
          if (ty < -FONT_SIZE || ty > height + FONT_SIZE) continue

          const fade = (1 - t / trailSteps) * col.brightness * rainStrength
          const isHead = t === 0
          const alpha = isHead ? fade * 0.95 : fade * 0.4

          if (alpha < 0.03) continue

          if (isHead) {
            ctx.fillStyle = `rgba(${headR}, ${headG}, ${headB}, ${alpha})`
          } else {
            ctx.fillStyle = `rgba(${dimR}, ${dimG}, ${dimB}, ${alpha * 0.55})`
          }

          const char = t === 0 ? col.char : pickChar()
          ctx.fillText(char, x, ty)
        }
      }

      raf = requestAnimationFrame(draw)
    }

    resize()
    raf = requestAnimationFrame(draw)

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    window.addEventListener('resize', resize, { passive: true })

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('resize', resize)
    }
  }, [reducedMotion, isDarkMode])

  return (
    <canvas
      ref={canvasRef}
      className="fixer-ascii-rain"
      aria-hidden="true"
    />
  )
}

interface FixerAsciiRainLayerProps {
  scrollRootRef: RefObject<HTMLElement | null>
  heroPanelRef: RefObject<HTMLElement | null>
  reducedMotion?: boolean
  isDarkMode?: boolean
}

/** Fixed layer: rain reveals on scroll over hero subject, then pours down the page. */
export function FixerAsciiRainLayer({
  scrollRootRef,
  heroPanelRef,
  reducedMotion = false,
  isDarkMode = true,
}: FixerAsciiRainLayerProps) {
  const { scrollTop, intensity, expand } = useFixerRainScroll(scrollRootRef, heroPanelRef)

  // Subject sits ~38% x / 40% y in hero frame (object-position center 42%)
  const subjectX = 38 - expand * 6
  const subjectY = 40 + expand * 32
  const ellipseW = 42 + expand * 68
  const ellipseH = 26 + expand * 94
  const maskInner = 28 + expand * 32

  const mask = `radial-gradient(ellipse ${ellipseW}% ${ellipseH}% at ${subjectX}% ${subjectY}%, black 0%, black ${maskInner}%, transparent 78%)`

  return (
    <div
      className="fixer-rain-scroll-host"
      style={{
        opacity: intensity * 0.88,
        WebkitMaskImage: mask,
        maskImage: mask,
      }}
      aria-hidden="true"
    >
      <FixerAsciiRain
        scrollTop={scrollTop}
        intensity={intensity}
        reducedMotion={reducedMotion}
        isDarkMode={isDarkMode}
      />
    </div>
  )
}
